<template>
  <UHeader
    v-bind="$attrs"
    :title="columnName"
    :toggle="false"
    class="p-header column-handle static! z-auto! border-0! backdrop-blur-none! select-none max-sm:[&>[data-slot=container]]:hidden"
    :class="{
      'sm:[&>[data-slot=container]]:pt-2.5 sm:[&>[data-slot=container]]:pb-0.5': showTitle
    }"
  >
    <template v-if="slots.top" #top><slot name="top" /></template>
    <template #left>
      <slot name="left">
        <div class="min-w-0">
          <div
            v-if="showTitle"
            class="font-heading hidden items-center gap-x-2 text-sm select-none sm:flex"
          >
            <slot name="icon">
              <FontAwesomeIcon
                v-if="columnIcon"
                :icon="columnIcon"
                class="shrink-0"
                aria-hidden="true"
              />
            </slot>
            <span
              ><slot name="title">{{ columnName }}</slot></span
            >
          </div>
        </div>
      </slot>
    </template>
    <template #right>
      <slot name="right">
        <div v-if="showTitle" class="hidden sm:block" :style="closeButtonWrapperStyle">
          <PButton
            v-if="isLayoutColumnClosable"
            color="primary"
            name="close-column"
            class="p-0!"
            :icon="['fas', 'xmark']"
            :disabled="isCloseDisabled"
            title="Kolom sluiten"
            @click="closeColumn"
          />
        </div>
      </slot>
    </template>
    <template v-if="slots.toggle" #toggle="slotProps">
      <slot name="toggle" v-bind="slotProps" />
    </template>
    <template #bottom>
      <slot name="bottom">
        <div
          class="primary flex min-h-10 items-start justify-between space-x-2 overflow-x-clip px-2 pb-2 max-sm:pt-2 sm:space-x-0 sm:rounded-b-md"
        >
          <div class="flex w-full justify-between space-x-2">
            <div class="grow">
              <slot />
            </div>
            <div v-if="hasSearch && showSearchButton">
              <PChip
                :show="filterCount > 0"
                :text="filterCount"
                :color="searchExpanded ? 'surface' : 'secondary'"
                size="3xl"
                ping
                @click="toggleSearch"
              >
                <PButton
                  :color="searchExpanded ? 'secondary' : 'surface'"
                  title="Zoeken"
                  :icon="['fas', 'magnifying-glass']"
                />
              </PChip>
            </div>
          </div>
          <PSidebar v-if="showNavigationMenu" class="sm:hidden" />
        </div>
        <slot v-if="hasSearch && searchExpanded" name="search">
          <div class="mt-2">
            <SearchBar v-model="defaultSearchText" :placeholder="searchPlaceholder" />
          </div>
        </slot>
        <div
          v-if="$slots.filters"
          v-show="searchExpanded"
          class="gray-lighten-2 mt-2 space-y-2 rounded-md p-2"
        >
          <slot name="filters" />
          <PButton
            variant="outline"
            size="sm"
            v-if="$slots['filters-expanded']"
            @click="toggleFiltersExpanded"
            :icon="['fas', filtersExpanded ? 'chevron-up' : 'chevron-down']"
            class="mb-0"
          >
            Meer filters
          </PButton>
          <div v-if="$slots['filters-expanded']" v-show="filtersExpanded">
            <slot name="filters-expanded" />
          </div>
        </div>
      </slot>
    </template>
    <template v-if="slots.body" #body><slot name="body" /></template>
    <template v-if="slots.content" #content="slotProps">
      <slot name="content" v-bind="slotProps" />
    </template>
  </UHeader>
</template>

<script setup lang="ts">
import { inject, computed, type ComputedRef, ref } from "vue"
import PButton from "@/components/elements/PButton.vue"
import UHeader, { type HeaderSlots } from "@nuxt/ui/components/Header.vue"
import PChip from "@/components/elements/PChip.vue"
import SearchBar from "@/components/misc/SearchBar.vue"
import type { LayoutColumn } from "@/models/user_settings/layout-column.model"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import PSidebar from "@/components/layout/PSidebar.vue"
import type { LayoutController } from "@/controllers/layout-controller.ts"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

// Inject the layout column from the parent LayoutColumn component
const layoutColumn = inject<ComputedRef<LayoutColumn> | null>("layoutColumn", null)

const isLayoutColumnClosable = inject("isLayoutColumnClosable", false)

const layoutColumnsStore = useLayoutColumnsStore()

const emit = defineEmits(["search-collapsed"])

const slots = defineSlots<
  HeaderSlots & {
    icon?(): unknown
    search?(): unknown
    filters?(): unknown
    "filters-expanded"?(): unknown
  }
>()

const searchText = defineModel<string>("searchText")

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    title?: string
    titleSuffix?: string
    showTitle?: boolean
    showSearchButton?: boolean
    showNavigationMenu?: boolean
    filterCount?: number
    searchPlaceholder?: string
  }>(),
  {
    showTitle: true,
    showSearchButton: true,
    showNavigationMenu: true,
    filterCount: 0,
    searchPlaceholder: "Zoeken..."
  }
)

const defaultSearchText = computed({
  get: () => searchText.value ?? "",
  set: (value: string) => {
    searchText.value = value
  }
})

const hasSearch = computed(() => Boolean(slots.search) || searchText.value !== undefined)

const columnName = computed(() => {
  const title = props.title ?? layoutColumn?.value?.getName() ?? ""
  return [title, props.titleSuffix].filter(Boolean).join(" - ")
})
const columnIcon = computed(() => layoutColumn?.value?.getIcon())

const isLastColumn = computed(() => {
  if (!layoutColumn?.value?.id || !layoutColumnsStore.layoutColumns.length) {
    return false
  }
  const currentIndex = layoutColumnsStore.layoutColumns.findIndex(
    (column) => column.id === layoutColumn.value.id
  )
  return currentIndex === layoutColumnsStore.layoutColumns.length - 1
})

const closeButtonWrapperStyle = computed(() => ({
  marginRight: isLastColumn.value
    ? "max(4rem, var(--p-header-right-offset, 0px))"
    : "var(--p-header-right-offset, 0px)"
}))

const isCloseDisabled = computed(() => {
  const columns = layoutColumnsStore.layoutColumns
  // Only allow closing the layout controls column if it is the only column.
  return (
    columns.length === 1 &&
    columns[0].component === LayoutColumnComponents.LAYOUT_CONTROLS &&
    layoutColumn?.value?.component === LayoutColumnComponents.LAYOUT_CONTROLS
  )
})

const layoutController: LayoutController = inject("layoutController")!
function closeColumn() {
  if (isCloseDisabled.value) {
    return
  }

  if (layoutColumn?.value?.id) {
    layoutController.removeColumn(layoutColumn.value.id)
  }
}

const searchExpanded = ref(false)

function toggleSearch() {
  searchExpanded.value = !searchExpanded.value
  if (!searchExpanded.value) {
    emit("search-collapsed")
  }
}

function expandSearch() {
  searchExpanded.value = true
}

function collapseSearch() {
  searchExpanded.value = false
  emit("search-collapsed")
}

defineExpose({ collapseSearch, expandSearch })

const filtersExpanded = ref(false)

function toggleFiltersExpanded() {
  filtersExpanded.value = !filtersExpanded.value
}
</script>

<style lang="scss" scoped></style>
